import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import BaseStockChart from "./BaseStockChart";
import SubTabNav from "../common/SubTabNav";
import { ChartDataService } from "../../services/ChartDataService";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { useChartCache } from "../../hooks/useChartCache";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import {
  usePresetMarkers,
  generatePresetPlotLines,
  generatePresetChartEvents,
  enhanceTooltipWithPresets,
  PresetToggle,
} from "./PresetMarkers";

// Helper function to get ISO week number
const getWeekNumber = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

interface OutputStockChartProps {
  machineSerialNo?: string;
  lineId?: string;
  shifts?: string[];
  className?: string;
  syncGroup?: string;
  activeTab?: "shift" | "day" | "week";
  onActiveTabChange?: (tab: "shift" | "day" | "week") => void;
  hideSubTabNav?: boolean; // Option to hide the subtab navigation when used in container
}

interface OutputDataPoint {
  date: string;
  output: number;
  shift: string;
  originalTimestamp?: string; // Store original timestamp (e.g., "2024-W40")
}

const OutputStockChart: React.FC<OutputStockChartProps> = ({
  machineSerialNo,
  lineId,
  shifts = [],
  className = "",
  syncGroup,
  activeTab: externalActiveTab,
  onActiveTabChange,
  hideSubTabNav = false,
}) => {
  const { t: _t } = useTranslation();
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const [rawChartData, setRawChartData] = useState<OutputDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalActiveTab, setInternalActiveTab] = useState<
    "shift" | "day" | "week"
  >("shift");
  const [zoomResetTrigger, setZoomResetTrigger] = useState<number>(0);
  const [showPresetMarkers, setShowPresetMarkers] = useState(false);

  // Use external state if provided, otherwise use internal state
  const activeTab =
    externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
  const setActiveTab = (tab: "shift" | "day" | "week") => {
    if (onActiveTabChange) {
      onActiveTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  // Use the preset markers hook
  const { presetData } = usePresetMarkers(machineSerialNo, showPresetMarkers);

  // Use chart cache for better performance
  const chartCache = useChartCache<OutputDataPoint[]>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 35, // Cache up to 35 different chart configurations
  });

  const fetchChartData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setRawChartData([]);
      setLoading(false);
      return;
    }

    // Either a machine (serialno) or a line (line_bk) is required.
    const entityId = machineSerialNo || lineId;
    if (!entityId || !dateRange.start || !dateRange.end) {
      return;
    }

    // Check cache first (separate key namespace for line vs machine)
    const cacheKey = lineId
      ? `pc.line_output.${activeTab}`
      : `pc.output.${activeTab}`;
    const cachedData = chartCache.getCachedData(
      cacheKey,
      entityId,
      dateRange,
      shifts,
    );
    if (cachedData) {
      setRawChartData(cachedData);
      return;
    }

    // Check authentication token
    if (!token) {
      setError("Authentication required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = lineId
        ? await ChartDataService.fetchLineOutput(
            ChartDataService.buildLineRequestParams(
              lineId,
              dateRange,
              shifts,
            ) as {
              line_bk: string;
              date_from: string;
              date_to: string;
              shift_name: string[];
            },
            activeTab,
            token,
          )
        : await ChartDataService.fetchOutput(
            ChartDataService.buildRequestParams(
              machineSerialNo,
              dateRange,
              shifts,
              activeTab,
            ),
            token,
          );

      console.log("📊 OutputStockChart: Raw API response:", {
        success: response.success,
        error: response.error,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataLength: Array.isArray(response.data) ? response.data.length : "N/A",
        sampleData: Array.isArray(response.data)
          ? response.data.slice(0, 3)
          : response.data,
        fullResponse: response,
      });

      // Process response
      const processedData: OutputDataPoint[] = [];

      if (response.success && response.data) {
        console.log(
          "📊 OutputStockChart: Response is successful, processing data...",
        );
        const responseData = response.data as any;

        // Extract actual data - backend returns { status, results, error }
        const results =
          responseData.results || responseData.data || responseData;

        console.log("📊 OutputStockChart: Extracted results:", {
          isArray: Array.isArray(results),
          type: typeof results,
          length: Array.isArray(results) ? results.length : "N/A",
          sample: Array.isArray(results) ? results.slice(0, 2) : results,
        });

        // Handle flat array format: [{shift_name, timestamp, output, machine_sn, exist_SAP}, ...]
        if (Array.isArray(results)) {
          console.log(
            "📊 OutputStockChart: Processing FLAT ARRAY format, items:",
            results.length,
          );
          results.forEach((item: any) => {
            const shift = item.shift_name || item.shift;
            const timestamp = item.timestamp;
            const output =
              typeof item.output === "number"
                ? item.output
                : parseFloat(item.output) || 0;

            // Only include if in selected shifts
            if (shifts.length === 0 || shifts.includes(shift)) {
              let date: string;

              if (activeTab === "week" && timestamp.includes("-W")) {
                // Handle ISO week format
                const [year, weekStr] = timestamp.split("-W");
                const weekNumber = parseInt(weekStr);
                const firstDayOfYear = new Date(parseInt(year), 0, 1);
                const firstWeekDay = firstDayOfYear.getDay();
                const firstWeek = firstWeekDay <= 4 ? 1 : 0;
                const weeksToAdd = weekNumber - firstWeek;
                const weekDate = new Date(firstDayOfYear);
                weekDate.setDate(
                  firstDayOfYear.getDate() + weeksToAdd * 7 - firstWeekDay + 1,
                );
                date = weekDate.toISOString().split("T")[0];
              } else {
                // Use full timestamp for proper sorting and display
                date = timestamp;
              }

              processedData.push({
                date,
                output,
                shift,
                originalTimestamp: timestamp,
              });
            }
          });
        } else if (results && typeof results === "object") {
          // Handle nested object format: { A1: { "2024-01-01": 123 }, ... } or { output: { ... } }
          const outputData = results.output || results;
          const availableShifts = Object.keys(outputData);

          console.log("📊 OutputStockChart: Processing NESTED OBJECT format:", {
            availableShifts,
            selectedShifts: shifts,
            sampleShiftData:
              availableShifts.length > 0
                ? {
                    shift: availableShifts[0],
                    timestamps: Object.keys(
                      outputData[availableShifts[0]],
                    ).slice(0, 3),
                  }
                : "none",
          });

          availableShifts.forEach((shift) => {
            const shiftData = outputData[shift];

            // Check if shift is in selected shifts
            const isSelected = shifts.length === 0 || shifts.includes(shift);
            console.log("📊 OutputStockChart: Shift check:", {
              shift,
              isSelected,
              selectedShifts: shifts,
            });

            if (isSelected) {
              Object.keys(shiftData).forEach((timestamp) => {
                let date: string;

                if (activeTab === "week" && timestamp.includes("-W")) {
                  const [year, weekStr] = timestamp.split("-W");
                  const weekNumber = parseInt(weekStr);
                  const firstDayOfYear = new Date(parseInt(year), 0, 1);
                  const firstWeekDay = firstDayOfYear.getDay();
                  const firstWeek = firstWeekDay <= 4 ? 1 : 0;
                  const weeksToAdd = weekNumber - firstWeek;
                  const weekDate = new Date(firstDayOfYear);
                  weekDate.setDate(
                    firstDayOfYear.getDate() +
                      weeksToAdd * 7 -
                      firstWeekDay +
                      1,
                  );
                  date = weekDate.toISOString().split("T")[0];
                } else {
                  // Handle timestamp format like "2016-05-22 07:30:00 +0000 UTC"
                  // Remove timezone suffix and parse
                  const cleanTimestamp = timestamp
                    .replace(" +0000 UTC", "")
                    .replace(" UTC", "");
                  const parsedDate = new Date(cleanTimestamp);

                  if (!isNaN(parsedDate.getTime())) {
                    date = parsedDate.toISOString().split("T")[0];
                  } else {
                    // Fallback: extract date part directly
                    date = cleanTimestamp.split(" ")[0];
                  }
                }

                const output =
                  typeof shiftData[timestamp] === "number"
                    ? shiftData[timestamp]
                    : parseFloat(shiftData[timestamp]) || 0;

                processedData.push({
                  date,
                  output,
                  shift,
                  originalTimestamp: timestamp,
                });
              });
            }
          });
        }
      } else {
        console.log("📊 OutputStockChart: Response failed or no data:", {
          success: response.success,
          hasData: !!response.data,
          error: response.error,
        });
      }

      // Sort by date
      processedData.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

      console.log("📊 OutputStockChart: Processed data:", {
        activeTab,
        dataLength: processedData.length,
        sampleData: processedData.slice(0, 5),
        shifts: [...new Set(processedData.map((p) => p.shift))],
        dateRange:
          processedData.length > 0
            ? {
                from: processedData[0].date,
                to: processedData[processedData.length - 1].date,
              }
            : null,
      });

      setRawChartData(processedData);

      // Cache the successful response
      chartCache.setCachedData(
        cacheKey,
        entityId,
        dateRange,
        shifts,
        processedData,
      );

      if (response.error) {
        setError(response.error);
      }
    } catch (error) {
      console.error("❌ OutputStockChart: Failed to fetch data:", error);
      setError(error instanceof Error ? error.message : "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchChartData();
  }, [
    machineSerialNo,
    lineId,
    dateRange.start,
    dateRange.end,
    shifts,
    activeTab,
  ]);

  // Reset zoom when activeTab changes
  useEffect(() => {
    setZoomResetTrigger((prev) => prev + 1);
  }, [activeTab]);

  // Transform data for BaseStockChart (similar to OutputChart but for time-series)
  const stockChartProps = useMemo(() => {
    // Group data by shift to create series
    const shiftGroups = rawChartData.reduce(
      (acc, item) => {
        if (!acc[item.shift]) {
          acc[item.shift] = [];
        }
        acc[item.shift].push(item);
        return acc;
      },
      {} as Record<string, OutputDataPoint[]>,
    );

    // Create series for each shift with timestamp data
    const series = Object.keys(shiftGroups).map((shift) => {
      const shiftData = shiftGroups[shift];

      // For 'day' and 'week' modes, we need to aggregate data and normalize timestamps
      // so all shifts on the same day/week share the same X position for stacking
      if (activeTab === "day") {
        // Aggregate by day - use date at midnight for consistent X position
        const dayAggregated: Record<string, number> = {};
        shiftData.forEach((item) => {
          const date = new Date(item.date);
          // Normalize to midnight UTC of that day
          const dayKey = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
          ).getTime();
          dayAggregated[dayKey] =
            (dayAggregated[dayKey] || 0) +
            (typeof item.output === "number"
              ? item.output
              : parseFloat(String(item.output)) || 0);
        });

        const seriesData: Array<[number, number]> = Object.entries(
          dayAggregated,
        )
          .map(([ts, val]) => [parseInt(ts), val] as [number, number])
          .sort((a, b) => a[0] - b[0]);

        return {
          name: `Shift ${shift}`,
          data: seriesData,
        };
      } else if (activeTab === "week") {
        // Aggregate by week - use Monday of the week for consistent X position
        const weekAggregated: Record<string, number> = {};
        shiftData.forEach((item) => {
          const date = new Date(item.date);
          // Get Monday of the week
          const day = date.getUTCDay();
          const diff = date.getUTCDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
          const monday = new Date(
            Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), diff),
          );
          const weekKey = monday.getTime();
          weekAggregated[weekKey] =
            (weekAggregated[weekKey] || 0) +
            (typeof item.output === "number"
              ? item.output
              : parseFloat(String(item.output)) || 0);
        });

        const seriesData: Array<[number, number]> = Object.entries(
          weekAggregated,
        )
          .map(([ts, val]) => [parseInt(ts), val] as [number, number])
          .sort((a, b) => a[0] - b[0]);

        return {
          name: `Shift ${shift}`,
          data: seriesData,
        };
      } else {
        // Per shift - keep individual timestamps
        const seriesData: Array<[number, number]> = shiftData
          .map((item) => {
            const timestamp = new Date(item.date).getTime();
            const rawValue = item.output;
            const value =
              typeof rawValue === "number"
                ? rawValue
                : parseFloat(String(rawValue)) || 0;
            return [timestamp, value] as [number, number];
          })
          .filter(([ts, val]) => !isNaN(ts) && !isNaN(val))
          .sort((a, b) => a[0] - b[0]);

        return {
          name: `Shift ${shift}`,
          data: seriesData,
        };
      }
    });

    return {
      series,
      title: `Output Over Time (${activeTab === "week" ? "Weekly" : activeTab === "day" ? "Daily" : "Per Shift"})`,
      subtitle: `${machineSerialNo || lineId || "Nothing selected"} • ${series.length} shift${series.length !== 1 ? "s" : ""}`,
      yAxisTitle: "Output",
    };
  }, [rawChartData, activeTab, machineSerialNo, lineId]);

  // Debug log the chart props
  console.log("🔍 OutputStockChart: Chart props:", {
    rawDataLength: rawChartData.length,
    seriesCount: stockChartProps.series.length,
    seriesData: stockChartProps.series.map((s) => ({
      name: s.name,
      dataLength: s.data.length,
      sampleData: s.data.slice(0, 3),
    })),
    activeTab,
    sampleRawData: rawChartData.slice(0, 3),
  });

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Subtab navigation - conditionally rendered */}
      {!hideSubTabNav && (
        <SubTabNav
          tabs={[
            { key: "shift", label: "per Shift" },
            { key: "day", label: "per Day" },
            { key: "week", label: "per Week" },
          ]}
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab as "shift" | "day" | "week")}
        />
      )}

      {/* Stock Chart */}
      <BaseStockChart
        commentProps={{ chartType: "output", machineBK: machineSerialNo }}
        type="column"
        height={500}
        title={stockChartProps.title}
        subtitle={stockChartProps.subtitle}
        series={stockChartProps.series}
        forceResetZoom={zoomResetTrigger}
        yAxis={{
          min: 0,
          format: "{value}",
          title: stockChartProps.yAxisTitle,
        }}
        customOptions={{
          chart: {
            events: generatePresetChartEvents(),
          },
          xAxis: {
            plotLines: generatePresetPlotLines(presetData, showPresetMarkers),
            labels:
              activeTab === "week"
                ? {
                    formatter: function () {
                      // @ts-ignore - Highcharts axis label context
                      const date = new Date(this.value);
                      const year = date.getFullYear();
                      const weekNumber = getWeekNumber(date);
                      return `W${weekNumber}<br/>${year}`;
                    },
                    rotation: 0,
                    align: "center",
                    style: {
                      fontSize: "11px",
                    },
                  }
                : {
                    formatter: undefined, // Reset to default formatting
                    rotation: 0,
                    align: "center",
                    style: {
                      fontSize: "11px",
                    },
                  },
          },
          plotOptions: {
            column:
              activeTab === "shift"
                ? {
                    stacking: undefined, // Explicitly disable stacking for per shift
                    grouping: true,
                  }
                : {
                    stacking: "normal",
                    dataLabels: {
                      enabled: false,
                    },
                  },
          },
          tooltip: {
            formatter: enhanceTooltipWithPresets(
              function (this: any) {
                let tooltip = `<b>${new Date(this.x).toLocaleDateString()}</b><br/>`;

                // Add output data
                if (this.points && this.points.length > 0) {
                  this.points.forEach((point: any) => {
                    tooltip += `<span style="color:${point.series.color}">●</span> ${point.series.name}: <b>${point.y}</b><br/>`;
                  });
                }

                return tooltip;
              },
              presetData,
              showPresetMarkers,
            ),
          },
        }}
        loading={loading}
        error={error}
        onRetry={fetchChartData}
        legend={{ enabled: true, position: "bottom" }}
        tooltip={{ suffix: "" }}
        panZoom={{
          enabled: true,
          type: "x",
          mouseWheel: true,
          pinchZoom: true,
        }}
        navigator={{
          enabled: true,
          height: 80,
          // Navigator colors will be automatically themed by BaseStockChart
        }}
        rangeSelector={{
          enabled: false,
        }}
        onRangeChange={(min, max) => {
          console.log("Range changed:", {
            from: new Date(min).toISOString(),
            to: new Date(max).toISOString(),
          });
        }}
        noDataMessage={
          !machineSerialNo && !lineId
            ? "Nothing selected"
            : !dateRange.start || !dateRange.end
              ? "Please select a date range"
              : !shifts || shifts.length === 0
                ? "No shifts selected or available for the selected timeframe"
                : "No output data found for the selected period"
        }
        syncGroup={syncGroup}
        extraControls={
          <PresetToggle
            enabled={showPresetMarkers}
            onToggle={setShowPresetMarkers}
            presetCount={presetData.length}
          />
        }
      />
    </div>
  );
};

export default OutputStockChart;
